import * as React from 'react';
import { SOCIAL_AUTH_PROVIDER_LABEL, SocialAuthProvider } from 'constants/auth';
import { getIsNativePlatform } from 'utils/mobile/getIsNativePlatform';
import FaceBookIcon from 'svg/FaceBookIcon';
import GoogleIcon from 'svg/GoogleIcon';
import { Button } from 'components/Button';

interface Props {
  onSelect: (provider: SocialAuthProvider) => void;
  disabled?: boolean;
}

interface ProviderButton {
  id: SocialAuthProvider;
  icon: React.ReactNode;
  isEnabled: boolean;
}

const PROVIDERS: ProviderButton[] = [
  {
    id: 'google',
    // The icon has no intrinsic viewBox; without one it draws at ~70% of the box.
    icon: <GoogleIcon viewBox="0 0 20 20" className="h-5 w-5 shrink-0" />,
    isEnabled: true,
  },
  {
    id: 'facebook',
    icon: <FaceBookIcon className="h-5 w-5 shrink-0 text-[#1877F2]" />,
    // Facebook Login only works for people with a role on the Meta app until it clears App Review.
    isEnabled: process.env.ENABLE_FACEBOOK_LOGIN === 'true',
  },
  {
    id: 'vair',
    icon: <img src="/images/vair/vair-mark.png" alt="" className="h-5 w-5 shrink-0 dark:invert" />,
    isEnabled: process.env.ENABLE_VAIR_LOGIN === 'true',
  },
];

/**
 * Provider buttons shared by the login and signup forms. Hidden on native
 * (Capacitor) — popup OAuth isn't supported in that webview yet, and the VAIR redirect
 * returns to the web origin.
 */
export default function OAuthButtons({ onSelect, disabled }: Props) {
  if (getIsNativePlatform()) {
    return null;
  }

  return (
    <div className="flex w-full gap-ds-md max-sm:flex-col">
      {PROVIDERS.filter((provider) => provider.isEnabled).map(({ id, icon }) => (
        <Button
          key={id}
          variant="secondary"
          size="sm"
          sizeDesktop="md"
          type="button"
          disabled={disabled}
          onClick={() => onSelect(id)}
          aria-label={`Continue with ${SOCIAL_AUTH_PROVIDER_LABEL[id]}`}
          iconLeft={icon}
          className="min-w-0 flex-1 whitespace-nowrap px-ds-md lg:px-ds-md"
        >
          {SOCIAL_AUTH_PROVIDER_LABEL[id]}
        </Button>
      ))}
    </div>
  );
}
